import React, { useState, type ReactNode } from 'react';
import { MdMoreVert } from 'react-icons/md';
import {
  ChipMenu,
  IconButton,
  CHIP_MENU_ALIGN_RIGHT,
  CHIP_MENU_ITEM_TONE_DANGER,
  TOOLTIP_ALIGN_END,
} from '@aibuild/ui';
import { MenuAnchor, MenuPosition } from './AppActionsMenu.styled';

export interface AppAction {
  readonly id: string;
  readonly label: string;
  readonly icon: ReactNode;
  readonly onSelect: () => void;
  readonly danger?: boolean;
  readonly meta?: ReactNode;
  readonly disabled?: boolean;
}

interface AppActionsMenuProps {
  readonly ariaLabel: string;
  readonly actions: readonly AppAction[];
  readonly dataTestId: string;
  /** In a toolbar rather than over a card's corner. */
  readonly inline?: boolean;
  readonly icon?: ReactNode;
}

/** The kebab on a gallery card, or in a toolbar. Renders nothing when there is nothing to offer. */
const AppActionsMenu: React.FC<AppActionsMenuProps> = ({ ariaLabel, actions, dataTestId, inline = false, icon }) => {
  const [open, setOpen] = useState(false);
  if (actions.length === 0) return null;

  const select = (action: AppAction) => (event: React.MouseEvent) => {
    event.stopPropagation();
    setOpen(false);
    action.onSelect();
  };

  return (
    <MenuAnchor $inline={inline} data-testid={dataTestId} onClick={(event) => event.stopPropagation()}>
      <IconButton
        ariaLabel={ariaLabel}
        tooltip={inline ? ariaLabel : undefined}
        tooltipAlign={TOOLTIP_ALIGN_END}
        icon={icon ?? <MdMoreVert size={18} />}
        size="sm"
        variant={inline ? 'ghost' : 'tonal'}
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        dataTestId={`${dataTestId}__trigger`}
      />
      {open && (
        <MenuPosition>
          <ChipMenu
            onClose={() => setOpen(false)}
            align={CHIP_MENU_ALIGN_RIGHT}
            ariaLabel={ariaLabel}
          >
            <ChipMenu.List>
              {actions.map((action) => (
                <ChipMenu.Item
                  key={action.id}
                  icon={action.icon}
                  meta={action.meta}
                  disabled={action.disabled}
                  tone={action.danger ? CHIP_MENU_ITEM_TONE_DANGER : undefined}
                  onClick={select(action)}
                  dataTestId={`${dataTestId}__${action.id}`}
                >
                  {action.label}
                </ChipMenu.Item>
              ))}
            </ChipMenu.List>
          </ChipMenu>
        </MenuPosition>
      )}
    </MenuAnchor>
  );
};

export default AppActionsMenu;
